/**
 * 🎰 MEGA SLOT MACHINE - Machine à sous ultra réaliste
 * Vrais rouleaux qui tournent avec physique et sons
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GameSounds } from './GameSounds';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Symboles avec leurs valeurs visuelles
const SYMBOLS = [
  { emoji: '🍒', name: 'Cerise', color: '#DC2626' },
  { emoji: '🍋', name: 'Citron', color: '#EAB308' },
  { emoji: '🍊', name: 'Orange', color: '#F97316' },
  { emoji: '🍇', name: 'Raisin', color: '#7C3AED' },
  { emoji: '🍀', name: 'Trèfle', color: '#16A34A' },
  { emoji: '⭐', name: 'Étoile', color: '#FBBF24' },
  { emoji: '💎', name: 'Diamant', color: '#06B6D4' },
  { emoji: '7️⃣', name: 'Sept', color: '#EF4444' },
  { emoji: '🏆', name: 'Trophée', color: '#F59E0B' },
];

// Configuration des probabilités (identique au système existant)
const PRIZE_CONFIG = {
  stamp: { probability: 0.001, label: '🏆 TAMPON!', value: 'stamp', points: 0 },
  discount10: { probability: 0.001, label: '🎁 -10%', value: 'discount10', points: 0 },
  discount5: { probability: 0.003, label: '🎁 -5%', value: 'discount5', points: 0 },
  replay: { probability: 0.03, label: '🍀 Rejoue!', value: 'replay', points: 0 },
  points50: { probability: 0.005, label: '⭐ 50 pts', value: '50', points: 50 },
  points25: { probability: 0.02, label: '⭐ 25 pts', value: '25', points: 25 },
  points10: { probability: 0.10, label: '⭐ 10 pts', value: '10', points: 10 },
  points5: { probability: 0.84, label: '✨ 5 pts', value: '5', points: 5 },
};

const determineWinning = () => {
  const random = Math.random();
  let cumulative = 0;
  for (const [key, config] of Object.entries(PRIZE_CONFIG)) {
    cumulative += config.probability;
    if (random <= cumulative) return { key, ...config };
  }
  return { key: 'points5', ...PRIZE_CONFIG.points5 };
};

// Composant Rouleau individuel
const Reel = ({ spinning, finalSymbol, delay, onStop }) => {
  const [symbols, setSymbols] = useState([0, 1, 2]);
  const [offset, setOffset] = useState(0);
  const intervalRef = useRef(null);
  const symbolHeight = 80;
  
  useEffect(() => {
    if (spinning) {
      // Démarrer la rotation
      let speed = 50;
      let currentOffset = 0;
      
      intervalRef.current = setInterval(() => {
        currentOffset += symbolHeight;
        setOffset(currentOffset);
        
        // Changer les symboles quand on fait un tour complet
        if (currentOffset % (symbolHeight * 3) === 0) {
          setSymbols(prev => {
            const newSymbols = [...prev];
            newSymbols.unshift(Math.floor(Math.random() * SYMBOLS.length));
            newSymbols.pop();
            return newSymbols;
          });
        }
        
        // Jouer le son de tick
        if (currentOffset % symbolHeight === 0) {
          GameSounds.slotSpin();
        }
      }, speed);
      
      // Arrêter après le délai
      setTimeout(() => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          // Placer le symbole final
          setSymbols([
            (finalSymbol + 1) % SYMBOLS.length,
            finalSymbol,
            (finalSymbol - 1 + SYMBOLS.length) % SYMBOLS.length
          ]);
          setOffset(0);
          GameSounds.slotStop();
          onStop?.();
        }
      }, delay);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [spinning, finalSymbol, delay, onStop]);

  return (
    <div className="relative w-20 h-24 overflow-hidden rounded-xl bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 border-2 border-yellow-500 shadow-[inset_0_2px_10px_rgba(0,0,0,0.8)]">
      {/* Effet de reflet */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 pointer-events-none z-10" />
      
      {/* Ligne centrale (ligne gagnante) */}
      <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-yellow-400/50 z-20 shadow-[0_0_10px_rgba(251,191,36,0.5)]" />
      
      {/* Symboles */}
      <motion.div
        className="flex flex-col items-center"
        animate={{ y: spinning ? -offset % (symbolHeight * 3) : 0 }}
        transition={spinning ? { duration: 0.05, ease: 'linear' } : { type: 'spring', stiffness: 300, damping: 30 }}
      >
        {symbols.map((symbolIndex, i) => (
          <div
            key={i}
            className="w-20 h-24 flex items-center justify-center text-5xl"
            style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
          >
            {SYMBOLS[symbolIndex]?.emoji}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

// Effet de confettis
const Confetti = ({ active }) => {
  if (!active) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-30">
      {[...Array(50)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-3"
          initial={{
            x: '50%',
            y: '50%',
            scale: 0,
            rotate: 0,
          }}
          animate={{
            x: `${Math.random() * 100}%`,
            y: `${Math.random() * 100 + 100}%`,
            scale: [0, 1, 1],
            rotate: Math.random() * 720 - 360,
          }}
          transition={{
            duration: 2 + Math.random(),
            delay: i * 0.02,
            ease: 'easeOut',
          }}
          style={{
            backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#EC4899'][i % 5],
            borderRadius: Math.random() > 0.5 ? '50%' : '0%',
          }}
        />
      ))}
    </div>
  );
};

// Composant principal
export function MegaSlotMachine({ customerId, onWin, gameStatus, isTraining, onGameEnd }) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [reels, setReels] = useState([0, 0, 0]);
  const [stoppedReels, setStoppedReels] = useState(0);
  const [result, setResult] = useState(null);
  const [showWin, setShowWin] = useState(false);
  const [isJackpot, setIsJackpot] = useState(false);
  const leverRef = useRef(null);

  const spin = async () => {
    if (isSpinning) return;
    
    // Initialiser le son
    GameSounds.init();
    GameSounds.slotLever(); // Nouveau son de levier !
    
    setIsSpinning(true);
    setResult(null);
    setShowWin(false);
    setIsJackpot(false);
    setStoppedReels(0);

    // Déterminer le résultat basé sur les probabilités
    const winning = determineWinning();
    
    // Choisir les symboles finaux en fonction du gain
    let finalReels;
    if (winning.key === 'stamp' || winning.key === 'discount10') {
      // Jackpot - 3 symboles identiques (7 ou trophée)
      const jackpotSymbol = winning.key === 'stamp' ? 8 : 7; // 🏆 ou 7️⃣
      finalReels = [jackpotSymbol, jackpotSymbol, jackpotSymbol];
      setIsJackpot(true);
    } else if (winning.key === 'discount5' || winning.points >= 25) {
      // Bon gain - 2 symboles identiques
      const matchSymbol = Math.floor(Math.random() * 7) + 1;
      finalReels = [matchSymbol, matchSymbol, Math.floor(Math.random() * SYMBOLS.length)];
    } else {
      // Gain normal - symboles aléatoires
      finalReels = [
        Math.floor(Math.random() * SYMBOLS.length),
        Math.floor(Math.random() * SYMBOLS.length),
        Math.floor(Math.random() * SYMBOLS.length),
      ];
    }
    
    setReels(finalReels);
  };

  const handleReelStop = useCallback((index) => {
    setStoppedReels(prev => {
      const newCount = prev + 1;
      
      // Quand tous les rouleaux sont arrêtés
      if (newCount === 3) {
        setTimeout(async () => {
          const winning = determineWinning();
          setResult(winning);
          setShowWin(true);
          
          // Sons et effets de victoire
          if (winning.key === 'stamp' || winning.key === 'discount10') {
            GameSounds.bigWinFanfare(); // Fanfare améliorée pour jackpot !
          } else if (winning.key === 'replay') {
            GameSounds.replay(); // Son de replay !
          } else if (winning.key.startsWith('discount')) {
            GameSounds.giftSound(); // Son de cadeau !
          } else if (winning.points >= 25) {
            GameSounds.win();
          } else {
            GameSounds.coins();
          }
          
          // Enregistrer la partie
          try {
            const prizeType = winning.points > 0 ? 'points' : 
                             winning.key === 'replay' ? 'replay' :
                             winning.key === 'stamp' ? 'stamp' :
                             winning.key.startsWith('discount') ? 'discount' : 'nothing';
            
            const prizeValue = winning.points > 0 ? winning.value : 
                              winning.key.startsWith('discount') ? winning.key : winning.value;
            
            const response = await api.recordGamePlay(
              customerId,
              'slots',
              prizeType,
              prizeValue,
              null,
              isTraining
            );
            
            if (response.message) {
              toast.success(response.message);
            }
            
            if (onWin) onWin(winning);
            if (onGameEnd) onGameEnd();
          } catch (error) {
            console.error('Erreur enregistrement:', error);
          }
          
          setIsSpinning(false);
        }, 500);
      }
      
      return newCount;
    });
  }, [customerId, isTraining, onWin, onGameEnd]);

  return (
    <div className="relative flex flex-col items-center p-6">
      {/* Confettis */}
      <Confetti active={isJackpot && showWin} />
      
      {/* Mode entraînement */}
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      {/* Titre avec effet néon */}
      <h3 className="text-3xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 animate-pulse">
        🎰 MEGA JACKPOT 🎰
      </h3>
      
      {/* Machine */}
      <div className="relative p-6 rounded-3xl bg-gradient-to-b from-red-900 via-red-800 to-red-900 border-4 border-yellow-500 shadow-[0_0_30px_rgba(234,179,8,0.3)]">
        {/* Décoration top */}
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-6 py-1 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full border-2 border-yellow-300">
          <span className="text-black font-black text-sm">★ LPE CASINO ★</span>
        </div>
        
        {/* Lumières décoratives */}
        <div className="absolute top-2 left-2 right-2 flex justify-between">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="w-3 h-3 rounded-full"
              animate={{
                backgroundColor: isSpinning 
                  ? ['#FFD700', '#FF0000', '#00FF00', '#FFD700']
                  : '#FFD700',
              }}
              transition={{
                duration: 0.3,
                repeat: isSpinning ? Infinity : 0,
                delay: i * 0.1,
              }}
            />
          ))}
        </div>
        
        {/* Zone des rouleaux */}
        <div className="flex gap-3 p-4 bg-black/50 rounded-2xl border-2 border-yellow-600/50 mt-4">
          <Reel 
            spinning={isSpinning} 
            finalSymbol={reels[0]} 
            delay={2000}
            onStop={() => handleReelStop(0)}
          />
          <Reel 
            spinning={isSpinning} 
            finalSymbol={reels[1]} 
            delay={2500}
            onStop={() => handleReelStop(1)}
          />
          <Reel 
            spinning={isSpinning} 
            finalSymbol={reels[2]} 
            delay={3000}
            onStop={() => handleReelStop(2)}
          />
        </div>
        
        {/* Bouton SPIN */}
        <motion.button
          ref={leverRef}
          onClick={spin}
          disabled={isSpinning}
          className="mt-6 w-full py-4 rounded-2xl font-black text-xl text-white shadow-lg"
          style={{
            background: isSpinning 
              ? 'linear-gradient(180deg, #666 0%, #444 100%)' 
              : 'linear-gradient(180deg, #22C55E 0%, #16A34A 50%, #15803D 100%)',
            boxShadow: isSpinning ? 'none' : '0 6px 0 #14532D, 0 8px 20px rgba(0,0,0,0.4)',
          }}
          whileHover={!isSpinning ? { scale: 1.02 } : {}}
          whileTap={!isSpinning ? { scale: 0.98, y: 4 } : {}}
        >
          {isSpinning ? (
            <span className="flex items-center justify-center gap-2">
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                🎰
              </motion.span>
              EN COURS...
            </span>
          ) : (
            <span>🎲 LANCER ! 🎲</span>
          )}
        </motion.button>
      </div>

      {/* Résultat */}
      <AnimatePresence>
        {showWin && result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className={`mt-6 p-6 rounded-2xl text-center ${
              result.key === 'stamp' || result.key === 'discount10'
                ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border-2 border-yellow-400'
                : result.points >= 25
                ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400'
                : 'bg-gray-800/50 border border-gray-600'
            }`}
          >
            <motion.span 
              className="text-4xl block mb-2"
              animate={result.key === 'stamp' ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.5, repeat: result.key === 'stamp' ? Infinity : 0 }}
            >
              {result.key === 'stamp' ? '🏆' : result.key.startsWith('discount') ? '🎁' : '⭐'}
            </motion.span>
            <span className="text-2xl font-black text-white">{result.label}</span>
            {isTraining && <p className="text-xs text-gray-400 mt-2">(Mode entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Légende des symboles */}
      <div className="mt-6 text-center">
        <p className="text-gray-500 text-xs">3x 🏆 = TAMPON GRATUIT • 3x 7️⃣ = -10%</p>
      </div>
    </div>
  );
}

export default MegaSlotMachine;
